import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  variant?: 'default' | 'searchPill';
}

const variants = {
  default: cn(
    'rounded-sm h-10 border border-border-subtle bg-bg-surface px-3.5 text-sm text-fg-2',
    'placeholder:text-fg-5',
    'hover:border-border-default',
    'focus-visible:border-border-focus focus-visible:ring-4 focus-visible:ring-purple-500/18 focus-visible:outline-none',
  ),
  // Signature Mindexia pill search — 2px border, gradient on focus.
  searchPill: cn(
    'rounded-pill h-11 border-2 border-border-subtle bg-bg-surface px-5 text-sm text-fg-2',
    'placeholder:text-fg-5',
    'hover:border-border-default',
    'focus-visible:outline-none focus-visible:[border:2px_solid_transparent]',
    'focus-visible:[background:linear-gradient(white,white)_padding-box,var(--gradient-pill-focus)_border-box]',
  ),
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, variant = 'default', ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'w-full transition-colors duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-50',
        variants[variant],
        invalid &&
          'border-danger-500 focus-visible:border-danger-500 focus-visible:ring-danger-500/20',
        className,
      )}
      {...props}
    />
  );
});
